import React, { useEffect, useMemo, useState } from 'react';
import { api, extractApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import RichTextEditor from '@/components/marketplace/RichTextEditor';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import SEOFieldsBlock from '@/components/marketplace/SEOFieldsBlock';
import SEOScoreBadge from '@/components/marketplace/SEOScoreBadge';
import { Plus, Pencil, Trash2, Archive, ArchiveRestore, ChevronRight, Loader2, Boxes } from 'lucide-react';

/**
 * AdminCategoriesTab — Phase 7D.1 · C7 Category Management CMS
 *
 * Layout:
 *   - Three sub-tabs (Sell / Wanted / Rent) since categories are scoped per listing type.
 *   - Two-level tree per sub-tab. Parents in first column, subcategories in the second.
 *   - Actions per row: Edit, Archive/Unarchive, Delete (only when listing_count=0 AND not system).
 *
 * Archive vs Delete:
 *   - Delete = removes the DB row. Blocked with 400 when listings reference it.
 *   - Archive = active=false + hidden=true. Safe for categories in use.
 */
const LISTING_TYPES = ['sell', 'wanted', 'rent', 'service'];

const AdminCategoriesTab = () => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);      // {cat?} for edit or {_new, listing_type, parent_id?}
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmArch, setConfirmArch] = useState(null);
  const [activeType, setActiveType] = useState('sell');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/categories/admin/list');
      setCats(data.categories || []);
    } catch (e) {
      toast.error(extractApiError(e, 'Failed to load categories'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Group into tree per listing_type
  const tree = useMemo(() => {
    const byType = { sell: [], wanted: [], rent: [], service: [] };
    const roots = cats.filter(c => !c.parent_id);
    for (const root of roots) {
      const children = cats.filter(c => c.parent_id === root.id);
      const bucket = byType[root.listing_type] || (byType[root.listing_type] = []);
      bucket.push({ ...root, children });
    }
    return byType;
  }, [cats]);

  const startNew = (listing_type, parent_id = null) => {
    setEditor({
      _new: true,
      name: '',
      listing_type,
      parent_id,
      display_order: 100,
      active: true,
      hidden: false,
      icon: '',
      focus_keyword: '',
      description: '',
    });
  };
  const startEdit = (cat) => setEditor({ ...cat });
  const closeEditor = () => setEditor(null);

  const save = async () => {
    if (!editor) return;
    if (!editor.name?.trim()) { toast.error('Name is required'); return; }
    // Pluck SEO fields once so they can be spread into both create & update
    // payloads. Empty string clears the field on the backend.
    const seoFields = {
      meta_title: editor.meta_title || '',
      meta_description: editor.meta_description || '',
      canonical_url: editor.canonical_url || '',
      og_image_url: editor.og_image_url || '',
      og_title: editor.og_title || '',
      og_description: editor.og_description || '',
      schema_type: editor.schema_type || '',
      robots_meta: editor.robots_meta || '',
      intro_html: editor.intro_html || '',
      content_html: editor.content_html || '',
      faqs: (editor.faqs || []).filter(f => (f?.q || '').trim() && (f?.a || '').trim()),
    };
    setSaving(true);
    try {
      if (editor._new) {
        const payload = {
          name: editor.name.trim(),
          listing_type: editor.listing_type,
          parent_id: editor.parent_id || null,
          display_order: Number(editor.display_order) || 0,
          active: !!editor.active,
          hidden: !!editor.hidden,
          icon: editor.icon || null,
          focus_keyword: (editor.focus_keyword || '').trim() || null,
          description: (editor.description || '').trim() || null,
          ...seoFields,
        };
        await api.post('/categories', payload);
        toast.success(`Category "${payload.name}" created`);
      } else {
        const payload = {
          name: editor.name.trim(),
          display_order: Number(editor.display_order) || 0,
          active: !!editor.active,
          hidden: !!editor.hidden,
          icon: editor.icon || null,
          focus_keyword: (editor.focus_keyword || '').trim() || '',
          description: (editor.description || '').trim() || '',
          ...seoFields,
        };
        await api.patch(`/categories/${editor.id}`, payload);
        toast.success(`Category "${payload.name}" updated`);
      }
      await load();
      closeEditor();
    } catch (e) {
      toast.error(extractApiError(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  const archive = async (cat) => {
    try {
      await api.post(`/categories/${cat.id}/archive`);
      toast.success(`"${cat.name}" archived`);
      setConfirmArch(null);
      await load();
    } catch (e) { toast.error(extractApiError(e, 'Archive failed')); }
  };
  const unarchive = async (cat) => {
    try {
      await api.post(`/categories/${cat.id}/unarchive`);
      toast.success(`"${cat.name}" restored`);
      await load();
    } catch (e) { toast.error(extractApiError(e, 'Restore failed')); }
  };
  const del = async (cat) => {
    try {
      await api.delete(`/categories/${cat.id}`);
      toast.success('Category deleted');
      setConfirmDel(null);
      await load();
    } catch (e) { toast.error(extractApiError(e, 'Delete failed')); }
  };

  if (loading) {
    return <Card className='p-6 text-sm text-muted-foreground' data-testid='admin-categories-loading'>
      <Loader2 className='inline h-4 w-4 animate-spin mr-2' /> Loading categories…
    </Card>;
  }

  return (
    <div className='space-y-4' data-testid='admin-categories-tab'>
      <Card className='p-4'>
        <div className='flex items-start justify-between gap-3'>
          <div>
            <div className='text-lg font-semibold flex items-center gap-2'><Boxes className='h-5 w-5 text-primary' /> Category management</div>
            <p className='text-sm text-muted-foreground mt-0.5'>
              Manage categories and subcategories per listing type. Categories in use by listings can be
              <strong> archived </strong>(hidden) but not deleted — this preserves buyer bookmarks and SEO.
            </p>
          </div>
        </div>
      </Card>

      <Tabs value={activeType} onValueChange={setActiveType}>
        <TabsList data-testid='cat-type-tabs'>
          {LISTING_TYPES.map(t => (
            <TabsTrigger key={t} value={t} data-testid={`cat-type-${t}`} className='capitalize'>
              {t} · {(tree[t] || []).length}
            </TabsTrigger>
          ))}
        </TabsList>

        {LISTING_TYPES.map(t => (
          <TabsContent key={t} value={t} className='space-y-3'>
            <div className='flex justify-end'>
              <Button size='sm' onClick={() => startNew(t)} data-testid={`cat-new-parent-${t}`}>
                <Plus className='h-4 w-4 mr-1' /> New parent category
              </Button>
            </div>
            {(tree[t] || []).length === 0 ? (
              <Card className='p-8 text-center text-sm text-muted-foreground'>No categories yet for {t}.</Card>
            ) : (
              <div className='space-y-3'>
                {tree[t].map(parent => (
                  <CategoryRow
                    key={parent.id}
                    cat={parent}
                    onEdit={() => startEdit(parent)}
                    onArchive={() => setConfirmArch(parent)}
                    onUnarchive={() => unarchive(parent)}
                    onDelete={() => setConfirmDel(parent)}
                    onAddChild={() => startNew(t, parent.id)}
                  >
                    {parent.children.length > 0 && (
                      <div className='mt-2 pl-6 border-l space-y-1.5'>
                        {parent.children.map(sub => (
                          <CategoryRow
                            key={sub.id}
                            cat={sub}
                            child
                            onEdit={() => startEdit(sub)}
                            onArchive={() => setConfirmArch(sub)}
                            onUnarchive={() => unarchive(sub)}
                            onDelete={() => setConfirmDel(sub)}
                          />
                        ))}
                      </div>
                    )}
                  </CategoryRow>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Editor dialog */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && closeEditor()}>
        <DialogContent className='max-w-2xl max-h-[85vh] overflow-y-auto' data-testid='cat-editor' onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{editor?._new ? 'New category' : `Edit category`}</DialogTitle>
            <DialogDescription>
              {editor?._new ? (
                editor.parent_id
                  ? `Adds a subcategory under an existing parent (${editor.listing_type}).`
                  : `Adds a top-level parent category for ${editor?.listing_type} listings.`
              ) : 'Update category name, order, or visibility. The listing type & parent are locked.'}
            </DialogDescription>
          </DialogHeader>
          {editor && (
            <div className='space-y-3'>
              <div>
                <Label htmlFor='cat-name'>Name</Label>
                <Input id='cat-name' value={editor.name || ''} onChange={e => setEditor({ ...editor, name: e.target.value })} placeholder='e.g. CNC Turning Machines' data-testid='cat-name-input' />
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <Label htmlFor='cat-order'>Display order</Label>
                  <Input id='cat-order' type='number' value={editor.display_order ?? 100} onChange={e => setEditor({ ...editor, display_order: Number(e.target.value) })} data-testid='cat-order-input' />
                </div>
                <div>
                  <Label htmlFor='cat-icon'>Icon name (lucide)</Label>
                  <Input id='cat-icon' value={editor.icon || ''} onChange={e => setEditor({ ...editor, icon: e.target.value })} placeholder='e.g. Factory' data-testid='cat-icon-input' />
                </div>
              </div>
              <div className='flex items-center justify-between rounded-md border p-3'>
                <div>
                  <div className='text-sm font-medium'>Active</div>
                  <div className='text-xs text-muted-foreground'>Inactive categories are excluded from public browsing.</div>
                </div>
                <Switch checked={!!editor.active} onCheckedChange={(v) => setEditor({ ...editor, active: v })} data-testid='cat-active-toggle' />
              </div>
              <div className='flex items-center justify-between rounded-md border p-3'>
                <div>
                  <div className='text-sm font-medium'>Hidden</div>
                  <div className='text-xs text-muted-foreground'>Hidden categories don&apos;t appear in the category tree or filters.</div>
                </div>
                <Switch checked={!!editor.hidden} onCheckedChange={(v) => setEditor({ ...editor, hidden: v })} data-testid='cat-hidden-toggle' />
              </div>
              <div className='pt-2 border-t space-y-3'>
                <div className='text-xs uppercase tracking-wider text-muted-foreground font-semibold'>SEO</div>
                <div>
                  <Label htmlFor='cat-focus-keyword'>Focus keyword</Label>
                  <Input
                    id='cat-focus-keyword'
                    value={editor.focus_keyword || ''}
                    maxLength={120}
                    placeholder='e.g. CNC turning machines'
                    onChange={e => setEditor({ ...editor, focus_keyword: e.target.value })}
                    data-testid='cat-focus-keyword-input'
                  />
                  <div className='text-[11px] text-muted-foreground mt-1'>
                    The primary phrase this category should rank for in search engines.
                  </div>
                </div>
                <div>
                  <Label htmlFor='cat-description'>Description</Label>
                  <Textarea
                    id='cat-description'
                    rows={3}
                    maxLength={1000}
                    value={editor.description || ''}
                    placeholder='Short description shown on category pages and used for meta description.'
                    onChange={e => setEditor({ ...editor, description: e.target.value })}
                    data-testid='cat-description-input'
                  />
                  <div className='text-[11px] text-muted-foreground text-right mt-0.5'>{(editor.description || '').length}/1000</div>
                </div>
              </div>

              {/* Landing content — mirrors the Section Pages CMS (intro + main body + FAQ) */}
              <div className='pt-2 border-t space-y-3'>
                <div className='text-xs uppercase tracking-wider text-muted-foreground font-semibold'>Landing content</div>
                <div>
                  <Label htmlFor='cat-intro-html'>Intro content</Label>
                  <RichTextEditor
                    value={editor.intro_html || ''}
                    onChange={(html) => setEditor({ ...editor, intro_html: html })}
                  />
                </div>
                <div>
                  <Label htmlFor='cat-content-html'>Main content</Label>
                  <RichTextEditor
                    value={editor.content_html || ''}
                    onChange={(html) => setEditor({ ...editor, content_html: html })}
                  />
                </div>
                <div>
                  <div className='flex items-center justify-between mb-2'>
                    <Label>FAQs (shown on page + emitted as FAQPage schema)</Label>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => setEditor({ ...editor, faqs: [...(editor.faqs || []), { q: '', a: '' }] })}
                      data-testid='cat-add-faq'
                    >
                      <Plus className='h-3.5 w-3.5 mr-1' /> Add FAQ
                    </Button>
                  </div>
                  <div className='space-y-3'>
                    {(editor.faqs || []).map((f, i) => (
                      <div key={i} className='rounded border p-3 space-y-2' data-testid={`cat-faq-${i}`}>
                        <div className='flex gap-2'>
                          <Input
                            placeholder='Question'
                            value={f?.q || ''}
                            onChange={e => {
                              const faqs = [...(editor.faqs || [])];
                              faqs[i] = { ...faqs[i], q: e.target.value };
                              setEditor({ ...editor, faqs });
                            }}
                            data-testid={`cat-faq-q-${i}`}
                          />
                          <Button
                            size='icon'
                            variant='ghost'
                            onClick={() => setEditor({ ...editor, faqs: (editor.faqs || []).filter((_, idx) => idx !== i) })}
                            data-testid={`cat-faq-del-${i}`}
                          >
                            <Trash2 className='h-4 w-4 text-destructive' />
                          </Button>
                        </div>
                        <Textarea
                          rows={2}
                          placeholder='Answer'
                          value={f?.a || ''}
                          onChange={e => {
                            const faqs = [...(editor.faqs || [])];
                            faqs[i] = { ...faqs[i], a: e.target.value };
                            setEditor({ ...editor, faqs });
                          }}
                          data-testid={`cat-faq-a-${i}`}
                        />
                      </div>
                    ))}
                    {(editor.faqs || []).length === 0 && (
                      <p className='text-xs text-muted-foreground'>No FAQs — FAQPage schema will not be emitted (correct: never fake FAQ schema).</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Full SEO block — Review.pdf #11 extension to Categories */}
              <div className='pt-4 border-t'>
                <SEOFieldsBlock
                  values={editor}
                  onChange={(patch) => setEditor({ ...editor, ...patch })}
                  allowAdvanced
                  contentType='category'
                  compact
                  testIdPrefix='cat-seo'
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={closeEditor} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid='cat-save-btn'>
              {saving ? 'Saving…' : (editor?._new ? 'Create' : 'Save changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive confirmation */}
      <AlertDialog open={!!confirmArch} onOpenChange={(o) => !o && setConfirmArch(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive &ldquo;{confirmArch?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmArch?.listing_count > 0
                ? `${confirmArch.listing_count} listing(s) reference this category. They will remain valid but won't appear in browsing or filters until you restore the category.`
                : 'This category will be hidden from the public site. You can restore it anytime.'}
              {!confirmArch?.parent_id && ' All subcategories will also be hidden.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => archive(confirmArch)} data-testid='cat-archive-confirm'>Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{confirmDel?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the category. If any listings reference it, the delete will be rejected — archive it instead in that case.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => del(confirmDel)}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
              data-testid='cat-delete-confirm'
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Category row
// ---------------------------------------------------------------------------
const CategoryRow = ({ cat, child, onEdit, onArchive, onUnarchive, onDelete, onAddChild, children }) => {
  const isArchived = cat.active === false;
  const inUse = (cat.listing_count || 0) > 0;
  return (
    <Card
      className={`p-3 ${isArchived ? 'opacity-70 border-dashed' : ''}`}
      data-testid={`cat-row-${cat.id}`}
    >
      <div className='flex items-start gap-3'>
        <div className={`h-8 w-8 rounded-md border flex items-center justify-center shrink-0 ${child ? 'bg-secondary/40' : 'bg-secondary'}`}>
          {child ? <ChevronRight className='h-4 w-4 text-muted-foreground' /> : <Boxes className='h-4 w-4 text-primary' />}
        </div>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='font-medium text-sm'>{cat.name}</span>
            {isArchived && <Badge variant='outline' className='text-[10px] gap-1'><Archive className='h-3 w-3' /> Archived</Badge>}
            {cat.hidden && !isArchived && <Badge variant='outline' className='text-[10px]'>Hidden</Badge>}
            <Badge variant='secondary' className='text-[10px]'>{cat.listing_count || 0} listings</Badge>
            <span className='text-[10px] text-muted-foreground'>order: {cat.display_order ?? 100}</span>
            <SEOScoreBadge score={cat.seo_score} summary={cat.seo_score_summary} />
          </div>
          <div className='text-[10px] text-muted-foreground mt-0.5'>slug: <code>{cat.slug}</code></div>
        </div>
        <div className='flex items-center gap-1.5 shrink-0'>
          {!child && onAddChild && !isArchived && (
            <Button size='sm' variant='outline' onClick={onAddChild} data-testid={`cat-add-child-${cat.id}`} className='hidden sm:inline-flex'>
              <Plus className='h-3.5 w-3.5 mr-1' /> Sub
            </Button>
          )}
          <Button size='sm' variant='outline' onClick={onEdit} data-testid={`cat-edit-${cat.id}`}>
            <Pencil className='h-3.5 w-3.5 mr-1' /> Edit
          </Button>
          {isArchived ? (
            <Button size='sm' variant='outline' onClick={onUnarchive} data-testid={`cat-unarchive-${cat.id}`}>
              <ArchiveRestore className='h-3.5 w-3.5 mr-1' /> Restore
            </Button>
          ) : (
            <Button size='sm' variant='outline' onClick={onArchive} data-testid={`cat-archive-${cat.id}`}>
              <Archive className='h-3.5 w-3.5 mr-1' /> Archive
            </Button>
          )}
          {!inUse && (
            <Button size='sm' variant='outline' className='text-destructive hover:text-destructive' onClick={onDelete} data-testid={`cat-delete-${cat.id}`}>
              <Trash2 className='h-3.5 w-3.5' />
            </Button>
          )}
        </div>
      </div>
      {children}
    </Card>
  );
};

export default AdminCategoriesTab;
